import { Button, Flex, Table } from 'antd'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { useTreatmentServiceGetPregnancyMonitoringListEndpoint } from 'src/api/queries'
import { PregnancyMonitoringSortBy, PregnancyType } from 'src/api/requests'
import { Select, Text } from 'src/components'
import {
  usePregnancyConfig,
  usePregnancyColumns,
  PersonPregnancyCreateModal
} from 'src/features/persons/components'
import { useStyles } from 'src/features/persons/components/styles'
import { useDefaultSortingItem, useTablePagination } from 'src/hooks'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'
import {
  getOrderingDirection,
  normalizeOrdering,
  resetPagination
} from 'src/utils'

export const PersonPregnancyTable = () => {
  const { t } = useLocales()
  const { styles } = useStyles()
  const { id: personId } = useParams()
  const navigate = useNavigate()

  const pagination = useTablePagination()

  const { options } = usePregnancyConfig()

  const [isModalOpen, setIsModalOpen] = useState(false)

  const { columns } = usePregnancyColumns({
    onOpenDetails: (id) => {
      navigate(paths.patients.pregnancy.details(String(personId), id))
    }
  })

  const [pregnancyTypeId, setPregnancyTypeId] = useState('')
  const [ordering, setOrdering] = useState('')

  const { defaultSortingOption } = useDefaultSortingItem()

  const queryParams = {
    pageNumber: pagination.current,
    pageSize: pagination.pageSize,
    ...(ordering && {
      sortBy: normalizeOrdering(ordering) as PregnancyMonitoringSortBy,
      sortDirection: getOrderingDirection(ordering)
    }),
    ...(pregnancyTypeId && { pregnancyType: pregnancyTypeId as PregnancyType }),
    personId
  }

  const { data, isLoading } =
    useTreatmentServiceGetPregnancyMonitoringListEndpoint(queryParams)

  const onSelect = (val: string) => {
    setOrdering(val)
    resetPagination(pagination)
  }

  const onPregnancySelect = (val: string) => {
    setPregnancyTypeId(val)
    resetPagination(pagination)
  }

  const pregnancyTypeOptions = [
    defaultSortingOption,
    ...Object.values(PregnancyType).map((item) => ({
      value: item,
      label: t(`pregnancy.pregnancy_type_status.${item}`)
    }))
  ]

  const onToggle = () => setIsModalOpen((prev) => !prev)

  return (
    <Flex vertical gap={16}>
      <Flex align="center" justify="space-between">
        <Text className={styles.text}>{t('pregnancy.pregnancy')}</Text>

        <Flex gap={8}>
          <Select
            onSelect={onPregnancySelect}
            options={pregnancyTypeOptions}
            placeholder={t('pregnancy.pregnancy_type')}
          />

          <Select onSelect={onSelect} options={options} />

          <Button type="primary" onClick={onToggle}>
            {t('pregnancy.add_pregnancy')}
          </Button>
        </Flex>
      </Flex>

      <Table
        rowKey="id"
        scroll={{ x: 'max-content' }}
        loading={isLoading}
        columns={columns}
        dataSource={data?.data}
        pagination={{ ...pagination, total: data?.totalCount }}
      />

      {isModalOpen && <PersonPregnancyCreateModal onCancel={onToggle} />}
    </Flex>
  )
}
